import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Save, Send } from "lucide-react";

/** Admin: set effective sender (overrides SENDER_EMAIL env) and send a test email. */
export function SenderConfig({ t, currentSender, onSaved }) {
  const [sender, setSender] = useState(currentSender || "");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [domains, setDomains] = useState([]);
  useEffect(() => { api.get("/resend/domains").then((r) => setDomains(r.data.domains || [])).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/resend/sender", { sender_email: sender });
      toast.success(`${t("set.senderSaved")}: ${data.sender_email}`);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/resend/test-email", { to: testTo });
      toast.success(`${t("set.testSent")} → ${data.to}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border p-3 mb-5 bg-muted/20 grid gap-3 md:grid-cols-2" data-testid="sender-config">
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.senderEmail")}</Label>
        <div className="flex gap-2">
          <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="compras@tu-dominio.com" className="rounded-none h-8 text-xs" data-testid="sender-email-input" />
          <Button size="sm" className="rounded-none text-xs h-8" disabled={busy || !sender.includes("@")} onClick={save} data-testid="sender-save-btn"><Save size={12} className="mr-1" /> {t("common.save")}</Button>
        </div>
        <p className="text-[9px] text-muted-foreground">{t("set.senderHint")}</p>
        {domains.length > 0 && (
          <div className="flex flex-wrap gap-1 items-center text-[10px]" data-testid="resend-domains">
            <span className="text-muted-foreground">{t("set.verifiedDomains")}:</span>
            {domains.map((d) => (
              <button key={d.id} type="button" onClick={() => setSender(`compras@${d.name}`)} data-testid={`resend-domain-${d.name}`}
                className={`border px-1.5 py-0.5 font-bold ${d.status === "verified" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                {d.name} · {d.status}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.testEmail")}</Label>
        <div className="flex gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tu@correo.com" className="rounded-none h-8 text-xs" data-testid="test-email-input" />
          <Button size="sm" variant="outline" className="rounded-none text-xs h-8" disabled={busy || !testTo.includes("@")} onClick={test} data-testid="test-email-btn"><Send size={12} className="mr-1" /> {t("set.sendTest")}</Button>
        </div>
        <p className="text-[9px] text-muted-foreground">{t("set.testEmailHint")}</p>
      </div>
    </div>
  );
}
